import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Radio, Loader2, ExternalLink, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getMedalStyle } from '@/lib/medals';
import { RunnerCard, type RunnerDisplayData } from '@/components/runners/RunnerCard';
import { RunnerStatsModal } from '@/components/runners/RunnerStatsModal';
import { RunnerFilterBar } from '@/features/races/detail/RunnerFilterBar';
import { ScoreBadge } from '@/features/races/detail/ScoreBadge';
import { getLiveStatusLabel, getLiveStatusColor } from '@/features/races/detail/liveStatusLabel';
import { GET_LIVE_TRACKING } from '@/graphql/queries';
import { Gender, LiveStatus, type GetLiveTrackingQuery, type GetRaceDetailsQuery } from '@/gql/graphql';

type LiveRunner = NonNullable<NonNullable<GetLiveTrackingQuery['liveTracking']>['runners']>[number];
type RaceRunner = NonNullable<NonNullable<GetRaceDetailsQuery['race']>['runners']>[number];

const POLL_INTERVAL = 3 * 60 * 1000;

const GENDER_TO_QUERY: Record<'ALL' | 'MALE' | 'FEMALE', Gender | null> = {
    ALL: null,
    MALE: Gender.Male,
    FEMALE: Gender.Female,
};

function toDisplayData(runner: LiveRunner, gender: 'MALE' | 'FEMALE' | null): RunnerDisplayData {
    return {
        name: runner.fullname,
        pictureUri: runner.photo,
        gender,
        utmbIndex: runner.index,
        nationality: runner.nationality,
    };
}

function getRank(runner: LiveRunner, rankKey: 'sexRanking' | 'scratchRanking'): number {
    return runner[rankKey] ?? 0;
}

function sortRunners(runners: LiveRunner[], rankKey: 'sexRanking' | 'scratchRanking'): LiveRunner[] {
    const ranked = runners.filter(r => getRank(r, rankKey) > 0)
        .sort((a, b) => getRank(a, rankKey) - getRank(b, rankKey));
    const dnf = runners.filter(r => getRank(r, rankKey) === 0 && r.status === LiveStatus.Dnf);
    const dns = runners.filter(r => getRank(r, rankKey) === 0 && r.status === LiveStatus.Dns);
    const other = runners.filter(r => getRank(r, rankKey) === 0 && r.status !== LiveStatus.Dnf && r.status !== LiveStatus.Dns);
    return [...ranked, ...other, ...dnf, ...dns];
}

function buildLiveRunnerUrl(utmbLiveUrl: string, bib: number): string {
    const parts = utmbLiveUrl.split('/');
    parts.pop();
    return `${parts.join('/')}/runners/${bib}`;
}

interface LiveRunnerListProps {
    raceId: string;
    utmbLiveUrl?: string | null;
    staticRunners?: RaceRunner[];
    raceFormat?: string | null;
}

export function LiveRunnerList({ raceId, utmbLiveUrl, staticRunners, raceFormat }: LiveRunnerListProps) {
    const { t } = useTranslation('races');
    const [genderFilter, setGenderFilter] = useState<'ALL' | 'MALE' | 'FEMALE'>('MALE');
    const [searchTerm, setSearchTerm] = useState('');
    const [myBetsOnly, setMyBetsOnly] = useState(false);
    const [minutesAgo, setMinutesAgo] = useState(0);
    const [selectedRunner, setSelectedRunner] = useState<{ live: LiveRunner; static: RaceRunner | null } | null>(null);

    const queryGender = GENDER_TO_QUERY[genderFilter];
    const rankKey = genderFilter === 'ALL' ? 'scratchRanking' : 'sexRanking';
    const displayGender = genderFilter === 'MALE' ? 'MALE' as const : genderFilter === 'FEMALE' ? 'FEMALE' as const : null;

    const { data, loading } = useQuery(GET_LIVE_TRACKING, {
        variables: { input: { raceId, gender: queryGender } },
        pollInterval: POLL_INTERVAL,
    });

    const fetchedAt = data?.liveTracking?.fetchedAt;

    // Static runners map for modal (utmbUri → RaceRunner)
    const staticRunnersMap = useMemo(() => {
        const map = new Map<string, RaceRunner>();
        for (const r of staticRunners ?? []) {
            if (r.utmbUri) map.set(r.utmbUri, r);
        }
        return map;
    }, [staticRunners]);

    // Score map (utmbUri → score + bet info), filtered by current gender view
    const userScoreMap = useMemo(() => {
        const map = new Map<string, { score: number; predictedPosition: number; tokens: number }>();
        const scores = data?.liveTracking?.currentUserScoreBreakdown?.runners;
        if (!scores) return map;
        for (const s of scores) {
            // In gender-specific mode, only show scores for that gender
            if (genderFilter !== 'ALL' && s.gender !== genderFilter) continue;
            if (s.utmbUri) map.set(s.utmbUri, { score: s.score ?? 0, predictedPosition: s.predictedPosition ?? 0, tokens: s.tokens ?? 1 });
        }
        return map;
    }, [data, genderFilter]);

    // Relative timestamp
    useEffect(() => {
        if (!fetchedAt) return;
        const update = () => {
            const diff = Math.floor((Date.now() - new Date(fetchedAt).getTime()) / 60000);
            setMinutesAgo(diff);
        };
        update();
        const interval = setInterval(update, 30_000);
        return () => clearInterval(interval);
    }, [fetchedAt]);

    // Sort + filter runners
    const runners = useMemo(() => {
        if (!data?.liveTracking?.runners) return [];
        let list = sortRunners([...data.liveTracking.runners], rankKey);
        if (myBetsOnly) {
            list = list.filter(r => r.utmbUri && userScoreMap.has(r.utmbUri));
        }
        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            list = list.filter(r => (r.fullname ?? '').toLowerCase().includes(term));
        }
        return list;
    }, [data, rankKey, searchTerm, myBetsOnly, userScoreMap]);

    const totalCount = data?.liveTracking?.runners?.length ?? 0;

    const handleRunnerClick = (runner: LiveRunner) => {
        const staticRunner = staticRunnersMap.get(runner.utmbUri ?? '') ?? null;
        setSelectedRunner({ live: runner, static: staticRunner });
    };

    // Build modal props from selected runner
    const modalRunner = selectedRunner ? {
        name: selectedRunner.live.fullname,
        pictureUri: selectedRunner.live.photo ?? selectedRunner.static?.pictureUri,
        gender: selectedRunner.static?.gender,
        nationality: selectedRunner.static?.nationality,
        utmbUri: selectedRunner.live.utmbUri,
        utmbIndex: selectedRunner.live.index,
        dnfRatio: selectedRunner.static?.dnfRatio,
        longestFinishedRaceKm: selectedRunner.static?.longestFinishedRaceKm,
        formatStats: selectedRunner.static?.formatStats,
    } : null;

    const modalUserScore = selectedRunner?.live.utmbUri
        ? userScoreMap.get(selectedRunner.live.utmbUri)
        : undefined;

    const modalLiveUrl = selectedRunner?.live.utmbUri && utmbLiveUrl && selectedRunner.live.bib
        ? buildLiveRunnerUrl(utmbLiveUrl, selectedRunner.live.bib)
        : null;

    const modalLiveRanking = selectedRunner ? getRank(selectedRunner.live, rankKey) : null;
    const modalIsDnf = selectedRunner?.live.status === LiveStatus.Dnf;
    const modalIsDns = selectedRunner?.live.status === LiveStatus.Dns;

    return (
        <div className="space-y-4">
            {/* Reuse RunnerFilterBar (no age groups in live mode) */}
            <RunnerFilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                genderFilter={genderFilter}
                onGenderChange={(g) => { setGenderFilter(g); setSearchTerm(''); }}
                filteredCount={runners.length}
                totalCount={totalCount}
                showMyBetsFilter={userScoreMap.size > 0}
                myBetsOnly={myBetsOnly}
                onMyBetsChange={setMyBetsOnly}
            />

            {/* Timestamp + UTMB Live pill */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="relative flex h-4 w-4 items-center justify-center">
                        <Radio size={12} className="text-rose-500" />
                        <span className="absolute inset-0 rounded-full bg-rose-400 animate-ping opacity-30" />
                    </span>
                    <span className="text-[11px] text-rose-400 font-medium">
                        {minutesAgo === 0
                            ? t('details.liveTracking.justNow')
                            : t('details.liveTracking.ago', { minutes: minutesAgo })}
                    </span>
                </div>
                {utmbLiveUrl && (
                    <a
                        href={utmbLiveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-rose-50 text-rose-500 border border-rose-200 hover:bg-rose-100 hover:text-rose-600 transition-colors"
                    >
                        UTMB Live
                        <ExternalLink size={10} />
                    </a>
                )}
            </div>

            {/* Loading */}
            {loading && !data && (
                <div className="flex items-center justify-center py-16 text-slate-400">
                    <Loader2 size={28} className="animate-spin text-rose-500" />
                </div>
            )}

            {/* Empty / not started */}
            {!loading && runners.length === 0 && data?.liveTracking && (
                <div className="flex flex-col items-center justify-center py-12 text-rose-400 bg-rose-50/50 rounded-2xl border border-dashed border-rose-200">
                    <Radio size={32} className="mb-2 opacity-20" />
                    <p className="text-sm font-medium">{t('details.liveTracking.notStarted')}</p>
                </div>
            )}

            {/* Conditions not met */}
            {!loading && !data?.liveTracking && (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                    <AlertCircle size={32} className="mb-2 opacity-20" />
                    <p className="text-sm font-medium">{t('details.liveTracking.unavailable')}</p>
                </div>
            )}

            {/* Runner list */}
            {runners.length > 0 && (
                <div className="grid gap-3">
                    {runners.map((runner) => {
                        const ranking = getRank(runner, rankKey);
                        const isRanked = ranking > 0;
                        const userScore = userScoreMap.get(runner.utmbUri ?? '');

                        const isBetRunner = userScore != null;
                        const isDoubled = (userScore?.tokens ?? 1) > 1;

                        return (
                            <RunnerCard
                                key={runner.utmbUri}
                                runner={toDisplayData(runner, displayGender)}
                                isHighlight={isBetRunner}
                                className={cn(
                                    "border-0 shadow-sm ring-1 transition-all",
                                    isBetRunner ? "ring-emerald-200 bg-emerald-50/30" : "ring-slate-100 hover:ring-emerald-200"
                                )}
                                onClick={() => handleRunnerClick(runner)}
                                action={
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        {userScore && <ScoreBadge score={userScore.score} />}
                                        {isDoubled && (
                                            <span className="px-1 py-0.5 rounded text-[9px] font-black bg-amber-100 text-amber-700">x2</span>
                                        )}
                                        {isRanked ? (
                                            <div className={cn(
                                                "w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black",
                                                getMedalStyle(ranking)
                                            )}>
                                                {ranking}
                                            </div>
                                        ) : (
                                            <span className={cn("text-[11px] font-medium", getLiveStatusColor(runner.status))}>
                                                {getLiveStatusLabel(runner.status, t)}
                                            </span>
                                        )}
                                    </div>
                                }
                            />
                        );
                    })}
                </div>
            )}

            {/* Runner stats modal */}
            {selectedRunner && modalRunner && (
                <RunnerStatsModal
                    runner={modalRunner}
                    raceFormat={raceFormat}
                    onClose={() => setSelectedRunner(null)}
                    predictedPosition={modalUserScore?.predictedPosition}
                    liveScore={modalUserScore?.score}
                    liveRunnerUrl={modalLiveUrl}
                    livePosition={modalLiveRanking && modalLiveRanking > 0 ? modalLiveRanking : null}
                    liveIsDnf={modalIsDnf}
                    liveIsDns={modalIsDns}
                />
            )}
        </div>
    );
}
